'use client';

import { createContext, useContext, type ReactNode } from 'react';

// Entity types that can be resolved
export type EntityType = 'credential' | 'endpoint' | 'signal' | 'application';

// Resolved entity data
export type ResolvedCredential = {
  id: string;
  name: string;
  type?: string;
  role?: string;
  username?: string;
};

export type ResolvedEndpoint = {
  id: string;
  method: string;
  path: string;
  host?: string;
};

export type ResolvedSignal = {
  id: string;
  description: string;
  category?: string;
};

export type ResolvedApplication = {
  id: string;
  name: string;
};

export type ResolvedEntity =
  | ResolvedCredential
  | ResolvedEndpoint
  | ResolvedSignal
  | ResolvedApplication;

// Resolver functions
export type EntityResolvers = {
  credential?: (id: string) => ResolvedCredential | undefined;
  endpoint?: (id: string) => ResolvedEndpoint | undefined;
  signal?: (id: string) => ResolvedSignal | undefined;
  application?: (id: string) => ResolvedApplication | undefined;
};

// Pre-resolved entities map
export type ResolvedEntitiesMap = {
  credentials?: Record<string, ResolvedCredential>;
  endpoints?: Record<string, ResolvedEndpoint>;
  signals?: Record<string, ResolvedSignal>;
  applications?: Record<string, ResolvedApplication>;
};

type EntityResolverContextValue = {
  resolvers: EntityResolvers;
  entities: ResolvedEntitiesMap;
  resolve: <T extends EntityType>(
    type: T,
    id: string,
  ) => ResolvedEntity | undefined;
};

const EntityResolverContext = createContext<EntityResolverContextValue | null>(
  null,
);

export type EntityResolverProviderProps = {
  children: ReactNode;
  /** Resolver functions for each entity type */
  resolvers?: EntityResolvers;
  /** Pre-resolved entities for quick lookup */
  entities?: ResolvedEntitiesMap;
};

export function EntityResolverProvider({
  children,
  resolvers = {},
  entities = {},
}: EntityResolverProviderProps) {
  const resolve = <T extends EntityType>(
    type: T,
    id: string,
  ): ResolvedEntity | undefined => {
    // First try pre-resolved entities
    switch (type) {
      case 'credential':
        if (entities.credentials?.[id]) return entities.credentials[id];
        return resolvers.credential?.(id);
      case 'endpoint':
        if (entities.endpoints?.[id]) return entities.endpoints[id];
        return resolvers.endpoint?.(id);
      case 'signal':
        if (entities.signals?.[id]) return entities.signals[id];
        return resolvers.signal?.(id);
      case 'application':
        if (entities.applications?.[id]) return entities.applications[id];
        return resolvers.application?.(id);
      default:
        return undefined;
    }
  };

  return (
    <EntityResolverContext.Provider value={{ resolvers, entities, resolve }}>
      {children}
    </EntityResolverContext.Provider>
  );
}

export function useEntityResolver() {
  const context = useContext(EntityResolverContext);
  if (!context) {
    // Return a no-op resolver if not wrapped in provider
    return {
      resolvers: {},
      entities: {},
      resolve: () => undefined,
    };
  }
  return context;
}

// Utility to detect if a string looks like a MongoDB ObjectId
export function isMongoId(value: unknown): value is string {
  if (typeof value !== 'string') return false;
  return /^[a-f\d]{24}$/i.test(value);
}

// Utility to detect entity type from field name
export function detectEntityType(fieldName: string): EntityType | null {
  const lowerName = fieldName.toLowerCase();

  if (lowerName.includes('credential') || lowerName === 'credentialid') {
    return 'credential';
  }
  if (lowerName.includes('endpoint') || lowerName === 'endpointid') {
    return 'endpoint';
  }
  if (
    lowerName.includes('signal') ||
    lowerName === 'signalid' ||
    lowerName === 'signalid'
  ) {
    return 'signal';
  }
  if (
    lowerName.includes('application') ||
    lowerName === 'applicationid' ||
    lowerName === 'appid'
  ) {
    return 'application';
  }

  return null;
}
